import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  getResumenPagosMs2,
  listarPagosMs2,
  type EstadoPago,
  type LegajoPagoRow,
} from '@lib/services/pagos.service';
import { LinesSkeleton, Skeleton, TableSkeleton } from '@components/medicus-ui/Loaders';

const PAGE_SIZE = 50;

const ESTADO_LABEL: Record<EstadoPago, string> = {
  pendiente: 'Pendiente',
  aprobado: 'Aprobado',
  rechazado: 'Rechazado',
  cancelado: 'Cancelado',
};

const ESTADO_CLASS: Record<EstadoPago, string> = {
  pendiente: 'status status-warning',
  aprobado: 'status status-ok',
  rechazado: 'status status-error',
  cancelado: 'status status-mid',
};

function fmtMoney(n: number): string {
  return n.toLocaleString('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  });
}

function fmtDateTime(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function PagosMs2Page() {
  const [estado, setEstado] = useState<EstadoPago | ''>('');
  const [page, setPage] = useState(1);

  const resumenQ = useQuery({
    queryKey: ['pagos-ms2', 'resumen'],
    queryFn: getResumenPagosMs2,
    refetchInterval: 30_000,
  });

  const listaQ = useQuery({
    queryKey: ['pagos-ms2', 'lista', estado, page],
    queryFn: () =>
      listarPagosMs2({
        estado: estado || undefined,
        offset: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

  const resumen = resumenQ.data;
  const tasaCobro = resumen && resumen.montoComprometido > 0
    ? Math.round((resumen.montoCobrado / resumen.montoComprometido) * 100)
    : 0;

  const totalPages = Math.max(1, Math.ceil((listaQ.data?.total ?? 0) / PAGE_SIZE));

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Pagos MS2</h1>
        <p className="text-element-mid">
          Cobranza del plan superador: cuántos titulares lo eligieron, cuántos
          iniciaron el pago y cuánto realmente se está cobrando.
        </p>
      </header>

      <div className="grid grid-cols-3 gap-md" style={{ marginTop: 24 }}>
        <article className="card">
          <p className="text-element-mid">Eligieron MS2</p>
          <p className="heading-2 text-primary">
            {resumen ? (
              resumen.totalElegidoMs2.toLocaleString('es-AR')
            ) : resumenQ.isLoading ? (
              <Skeleton width={90} height={30} />
            ) : (
              '—'
            )}
          </p>
          <p className="text-element-mid" style={{ fontSize: 12 }}>
            {resumen
              ? `${resumen.conPagoIniciado.toLocaleString('es-AR')} iniciaron el pago`
              : ''}
          </p>
        </article>
        <article className="card">
          <p className="text-element-mid">Monto cobrado</p>
          <p className="heading-2 text-success">
            {resumen ? (
              fmtMoney(resumen.montoCobrado)
            ) : resumenQ.isLoading ? (
              <Skeleton width={140} height={30} />
            ) : (
              '—'
            )}
          </p>
          <p className="text-element-mid" style={{ fontSize: 12 }}>
            {resumen
              ? `de ${fmtMoney(resumen.montoComprometido)} comprometidos (${tasaCobro}%)`
              : ''}
          </p>
        </article>
        <article className="card">
          <p className="text-element-mid">Estado del backlog</p>
          {resumenQ.isLoading && <LinesSkeleton lines={4} />}
          {resumen ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
              <BadgeLinea label="Aprobados" n={resumen.porEstado.aprobado} klass="text-success" />
              <BadgeLinea label="Pendientes" n={resumen.porEstado.pendiente} klass="text-warning" />
              <BadgeLinea label="Rechazados" n={resumen.porEstado.rechazado} klass="text-error" />
              <BadgeLinea label="Cancelados" n={resumen.porEstado.cancelado} klass="text-element-mid" />
              {resumen.aprobadosConDiscrepancia > 0 && (
                <BadgeLinea
                  label="Con discrepancia ⚠"
                  n={resumen.aprobadosConDiscrepancia}
                  klass="text-warning"
                />
              )}
            </div>
          ) : (
            <p className="heading-2 text-element-mid">—</p>
          )}
        </article>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <div className="rowBetween" style={{ alignItems: 'baseline' }}>
          <h2 className="heading-5" style={{ margin: 0 }}>
            Legajos con pago iniciado
          </h2>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <label className="text-element-mid" style={{ fontSize: 12 }}>
              Estado:
            </label>
            <select
              className="input"
              style={{ minWidth: 160 }}
              value={estado}
              onChange={(e) => {
                setEstado(e.target.value as EstadoPago | '');
                setPage(1);
              }}
            >
              <option value="">Todos</option>
              <option value="pendiente">Pendientes</option>
              <option value="aprobado">Aprobados</option>
              <option value="rechazado">Rechazados</option>
              <option value="cancelado">Cancelados</option>
            </select>
          </div>
        </div>

        {listaQ.isLoading && (
          <div style={{ marginTop: 16 }}>
            <TableSkeleton rows={10} />
          </div>
        )}
        {listaQ.isError && (
          <p className="status status-error" style={{ display: 'block', marginTop: 16 }}>
            No se pudieron cargar los pagos.
          </p>
        )}
        {listaQ.data && listaQ.data.rows.length === 0 && (
          <p className="text-element-mid" style={{ marginTop: 16 }}>
            No hay pagos {estado ? `en estado "${ESTADO_LABEL[estado]}"` : 'todavía'}.
          </p>
        )}

        {listaQ.data && listaQ.data.rows.length > 0 && (
          <div className="tableWrap" style={{ marginTop: 16 }}>
            <table className="dataTable">
              <thead>
                <tr>
                  <th>Legajo</th>
                  <th>Titular</th>
                  <th>DNI</th>
                  <th>Empresa</th>
                  <th style={{ textAlign: 'right' }}>Monto</th>
                  <th>Estado</th>
                  <th style={{ textAlign: 'center' }}>Intentos</th>
                  <th>Iniciado</th>
                  <th>Actualizado</th>
                </tr>
              </thead>
              <tbody>
                {listaQ.data.rows.map((r) => (
                  <FilaPago key={r.legajoId} r={r} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {listaQ.data && listaQ.data.total > PAGE_SIZE && (
          <footer
            className="rowBetween"
            style={{
              alignItems: 'center', marginTop: 16, paddingTop: 12,
              borderTop: '1px solid var(--color-border, #E2E8F0)',
            }}
          >
            <span className="text-element-mid" style={{ fontSize: 12 }}>
              Mostrando {(page - 1) * PAGE_SIZE + 1}-
              {Math.min(page * PAGE_SIZE, listaQ.data.total)} de{' '}
              {listaQ.data.total.toLocaleString('es-AR')}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <button
                className="btn btn-ghost"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          </footer>
        )}
      </div>
    </section>
  );
}

function BadgeLinea({ label, n, klass }: { label: string; n: number; klass: string }) {
  return (
    <div className="rowBetween" style={{ alignItems: 'center' }}>
      <span className="text-element-mid" style={{ fontSize: 13 }}>{label}</span>
      <span className={`heading-5 ${klass}`} style={{ margin: 0 }}>
        {n.toLocaleString('es-AR')}
      </span>
    </div>
  );
}

function FilaPago({ r }: { r: LegajoPagoRow }) {
  const nombreCompleto = `${r.titular.nombres} ${r.titular.apellido}`.trim() || '—';
  const tituloDiscrepancia = r.montoDiscrepante
    ? `El gateway cobró ${fmtMoney(r.monto)} pero esperábamos ${fmtMoney(r.montoEsperado ?? 0)}.`
    : undefined;
  return (
    <tr>
      <td>
        <strong>{r.legajoNumero}</strong>
      </td>
      <td>{nombreCompleto}</td>
      <td>{r.titular.documento || '—'}</td>
      <td>{r.empresaNombre || '—'}</td>
      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {fmtMoney(r.monto)}
        {r.montoDiscrepante && r.montoEsperado != null && (
          <div
            className="text-warning"
            style={{ fontSize: 11, marginTop: 2 }}
            title={tituloDiscrepancia}
          >
            esperaba {fmtMoney(r.montoEsperado)}
          </div>
        )}
      </td>
      <td>
        {r.estado ? (
          r.montoDiscrepante ? (
            <span className="status status-warning" title={tituloDiscrepancia}>
              {ESTADO_LABEL[r.estado]} ⚠
            </span>
          ) : (
            <span className={ESTADO_CLASS[r.estado]}>{ESTADO_LABEL[r.estado]}</span>
          )
        ) : (
          <span className="text-element-mid">—</span>
        )}
      </td>
      <td style={{ textAlign: 'center' }}>{r.intentos}</td>
      <td>{fmtDateTime(r.iniciadoAt)}</td>
      <td>{fmtDateTime(r.actualizadoAt)}</td>
    </tr>
  );
}
